import { useContainsActiveElement } from '@app/utils/hooks/useContainsActiveElement';
import { useEtaLegend } from '@app/utils/hooks/useEtaLegend';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Dates, ItemEtasList, LegendItem, Product } from '@typings';
import React from 'react';
import { useHover, UseHoverProps } from 'react-laag';

import { useMatrixDeliveryWindowContext } from './MatrixDeliveryWindowContext';

interface Props {
  variants: (Product.Standard | Product.Full)[];
}

interface Context {
  activeEta: Dates.DateAsSQLString | null;
  containerRef: React.RefObject<HTMLDivElement>;
  hasActiveEta: boolean;
  hoverProps: UseHoverProps | Empty.Object;
  legendItems: LegendItem<Dates.DateAsSQLString>[];
  setActiveEta: (label: Dates.DateAsSQLString | null) => void;
  getShouldHighlightItemEta: (itemEtas?: ItemEtasList) => boolean;
  isEtaVisible: boolean;
  hasItemsWithEta: boolean;
}

export const useMatrixEtaContext = (): Context => {
  const context = React.useContext(MatrixEtaContext);

  if (!isDefined(context)) {
    throw new Error('MatrixEtaContext can not be used outside the scope of MatrixEtaContextProvider');
  }

  return context;
};

export const MatrixEtaContext = React.createContext<Context | null>(null);

export const MatrixEtaContextProvider = React.memo(({ children, variants }: React.WithChildren<Props>) => {
  const { deliveryWindowId } = useMatrixDeliveryWindowContext();

  const { hasActiveElement, containerRef } = useContainsActiveElement<HTMLDivElement>();
  const { hasItemsWithEta, legendItems } = useEtaLegend(variants, deliveryWindowId);
  const [isOver, hoverProps] = useHover({ hideOnScroll: false });
  const [activeEta, setActiveEta] = React.useState<Dates.DateAsSQLString | null>(null);

  const isEtaVisible = isOver || hasActiveElement;
  const getShouldHighlightItemEta = React.useCallback(
    (itemEtas?: ItemEtasList) => {
      if (!isDefined(itemEtas) || isEmpty(itemEtas)) {
        return false;
      }

      const [earliestDate] = itemEtas;

      return earliestDate === activeEta;
    },
    [activeEta],
  );

  return (
    <MatrixEtaContext.Provider
      value={{
        activeEta,
        containerRef,
        getShouldHighlightItemEta,
        hasActiveEta: isDefined(activeEta),
        hasItemsWithEta,
        hoverProps: hasItemsWithEta ? hoverProps : {},
        isEtaVisible,
        legendItems,
        setActiveEta,
      }}
    >
      {children}
    </MatrixEtaContext.Provider>
  );
});
